import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore
import { Pill } from '@els/els-react--pill';
import { AgeFilterTitles, HSearchFilters } from '../../../constants/h-search';

const SelectedPills = (props: any) => {
    const measurementRef = useRef<HTMLDivElement>(null);
    const rafIdRef = useRef<number | null>(null);
    const [sortedPills, setSortedPills] = useState<JSX.Element[]>([]);
    const { handleOnClosePill, currentFilters, searchFacetData } = props;

    const filterKeys: { [key: string]: string[] } = {
        'Clinical Skills': ['ageSpecific', 'speciality', 'category'],
        'Patient Education': ['subType', 'speciality'],
    };
    const { contentType } = currentFilters;
    const selectedKeysByContentType = contentType && filterKeys[contentType];

    const getFacetValueName = (value: string) => {
        if (searchFacetData && selectedKeysByContentType) {
            let result = '';
            selectedKeysByContentType.some((key: string) => {
                let facetName = key;
                const facetNameMap: { [key: string]: string } = {
                    speciality: HSearchFilters.SPECIALITIES,
                    category: HSearchFilters.CATEGORIES,
                    subType: HSearchFilters.PATIENTEDUCATIONTYPE,
                    ageSpecific: HSearchFilters.AGESPECIFIC,
                };
                facetName = facetNameMap[key] || key;
                const facet = searchFacetData.find(
                    (item: any) => item.facetName === facetName
                );

                if (facet) {
                    const facetValue = facet.facetValues.find((fv: any) => {
                        if (fv.of?.startsWith('+')) {
                            return fv.of?.slice(1) === value;
                        }
                        return fv.of === value;
                    });
                    if (facetValue) {
                        if (facetName === HSearchFilters.AGESPECIFIC) {
                            const normalizeAge = (val: string) => {
                                if (!val) return '';
                                return val
                                    .replace(/\+?age:?"?/g, '')
                                    .replace(/"/g, '')
                                    .replace(':', '')
                                    .trim();
                            };
                            const normalizedCurrent = normalizeAge(value);
                            const normalizedFacet = normalizeAge(facetValue.of);
                            if (normalizedCurrent === normalizedFacet) {
                                const ageEntry = Object.values(
                                    AgeFilterTitles
                                ).find(
                                    ([, ageVal]) =>
                                        normalizeAge(ageVal) ===
                                        normalizedCurrent
                                );
                                if (ageEntry) {
                                    const [ageName] = ageEntry;
                                    result = ageName;
                                    return true;
                                }
                            }
                        } else {
                            result = facetValue.name;
                            return true;
                        }
                    }
                }
                return false;
            });
            return result;
        }
        return '';
    };

    const generatePillsData = (
        keys: string[] | undefined,
        filters: any,
        getLabel: (value: string) => string,
        pillProps?: (
            key: string,
            value: string,
            label: string
        ) => Partial<React.ComponentProps<typeof Pill>>
    ) => {
        if (!keys) return [];
        return keys
            .filter((key: string) => {
                const value = filters[key];
                const label = getLabel(value);
                return value && label && label.trim() !== '';
            })
            .map((key: string) => {
                const value = filters[key];
                const label = getLabel(value);
                return {
                    key,
                    value,
                    label,
                    element: (
                        <Pill
                            key={value}
                            {...(pillProps ? pillProps(key, value, label) : {})}
                            label={<span aria-hidden>{label}</span>}
                            messages={{ REMOVE: `Remove ${label} filter` }}
                        />
                    ),
                };
            });
    };

    useEffect(() => {
        if (
            !selectedKeysByContentType ||
            selectedKeysByContentType.length === 0
        ) {
            setSortedPills([]);
            return;
        }

        const pillsData = generatePillsData(
            selectedKeysByContentType,
            currentFilters,
            getFacetValueName,
            (key, value) => ({
                className:
                    'u-els-margin-top-1o2 u-els-margin-bottom-1o4 u-els-margin-right-1o2',
                onClose: () => handleOnClosePill(key, value),
            })
        );

        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
            rafIdRef.current = null;
        }
        rafIdRef.current = requestAnimationFrame(() => {
            if (!measurementRef.current) {
                return;
            }
            const widths = Array.from(
                measurementRef.current.children || []
            ).map((child, index) => ({
                width: (child as HTMLElement).offsetWidth,
                ...pillsData[index],
            }));

            const sorted = widths.sort((a, b) => a.width - b.width);
            setSortedPills(sorted.map((p) => p.element));
        });

        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }
        };
    }, [currentFilters, searchFacetData, selectedKeysByContentType]);

    const measurementPills = useMemo(() => {
        const pillsData = generatePillsData(
            selectedKeysByContentType,
            currentFilters,
            getFacetValueName
        );
        return pillsData.map((p) => p.element);
    }, [selectedKeysByContentType, currentFilters]);

    return (
        <>
            <div
                ref={measurementRef}
                style={{
                    position: 'absolute',
                    visibility: 'hidden',
                    height: 0,
                    overflow: 'hidden',
                }}
            >
                {measurementPills}
            </div>
            <div>{sortedPills}</div>
        </>
    );
};

export default SelectedPills;
